import { completed, openDatabase, request } from './database.ts';
import type { BlobStore } from './types.ts';

export const CHUNK_SIZE = 8 * 1024 * 1024;

export class IdbStore implements BlobStore {
  constructor(private readonly db: IDBDatabase) {}
  static async open(): Promise<IdbStore> { return new IdbStore(await openDatabase()); }
  private range(sha: string): IDBKeyRange { return IDBKeyRange.bound(`${sha}:`, `${sha}:\uffff`); }
  async write(sha: string, offset: number, data: Uint8Array): Promise<void> {
    // Read/modify/write within one transaction, including unaligned resumed writes.
    const tx = this.db.transaction('chunks', 'readwrite');
    const done = completed(tx);
    const store = tx.objectStore('chunks');
    try {
      for (let pos = 0; pos < data.length;) {
        const index = Math.floor(offset / CHUNK_SIZE);
        const within = offset % CHUNK_SIZE;
        const length = Math.min(data.length - pos, CHUNK_SIZE - within);
        const key = `${sha}:${index}`;
        const old = await request<Uint8Array | undefined>(store.get(key));
        const chunk = new Uint8Array(Math.max(old?.length ?? 0, within + length));
        if (old) chunk.set(old);
        chunk.set(data.subarray(pos, pos + length), within);
        store.put(chunk, key);
        pos += length;
        offset += length;
      }
      await done;
    } catch (error) { await done.catch(() => {}); throw error; }
  }
  async truncate(sha: string, size: number): Promise<void> {
    const tx = this.db.transaction('chunks', 'readwrite');
    const done = completed(tx);
    const store = tx.objectStore('chunks');
    const req = store.openCursor(this.range(sha));
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      const index = Number(String(cursor.key).slice(sha.length + 1));
      if (index * CHUNK_SIZE >= size) cursor.delete();
      else if ((index + 1) * CHUNK_SIZE > size) cursor.update((cursor.value as Uint8Array).slice(0, size % CHUNK_SIZE));
      cursor.continue();
    };
    await done;
  }
  async *stream(sha: string): AsyncIterable<Uint8Array> {
    for (let index = 0; ; index++) {
      const chunk = await request<Uint8Array | undefined>(this.db.transaction('chunks').objectStore('chunks').get(`${sha}:${index}`));
      if (!chunk) return;
      yield chunk;
    }
  }
  async read(sha: string): Promise<Blob> {
    const chunks: BlobPart[] = [];
    for await (const chunk of this.stream(sha)) chunks.push(chunk as Uint8Array<ArrayBuffer>);
    return new Blob(chunks);
  }
  async remove(sha: string): Promise<void> { await this.truncate(sha, 0); }
}

export class OpfsStore implements BlobStore {
  constructor(private readonly directory: FileSystemDirectoryHandle) {}
  static async open(): Promise<OpfsStore> {
    const root = await navigator.storage.getDirectory();
    const directory = await root.getDirectoryHandle('replay-gamecache', { create: true });
    const probe = `probe-${crypto.randomUUID()}`;
    try {
      const handle = await directory.getFileHandle(probe, { create: true });
      const writer = await handle.createWritable();
      await writer.write(new Uint8Array([1]));
      await writer.close();
    } finally { await directory.removeEntry(probe).catch(() => {}); }
    return new OpfsStore(directory);
  }
  async write(sha: string, offset: number, data: Uint8Array): Promise<void> {
    const handle = await this.directory.getFileHandle(sha, { create: true });
    const writer = await handle.createWritable({ keepExistingData: true });
    try {
      await writer.seek(offset);
      await writer.write(data as Uint8Array<ArrayBuffer>);
      await writer.close();
    } catch (error) { await writer.abort().catch(() => {}); throw error; }
  }
  async truncate(sha: string, size: number): Promise<void> {
    const handle = await this.directory.getFileHandle(sha, { create: true });
    const writer = await handle.createWritable({ keepExistingData: true });
    try { await writer.truncate(size); await writer.close(); }
    catch (error) { await writer.abort().catch(() => {}); throw error; }
  }
  async read(sha: string): Promise<File> { return (await this.directory.getFileHandle(sha)).getFile(); }
  async *stream(sha: string): AsyncIterable<Uint8Array> {
    const reader = (await this.read(sha)).stream().getReader();
    try {
      while (true) { const part = await reader.read(); if (part.done) break; yield part.value; }
    } finally { reader.releaseLock(); }
  }
  async remove(sha: string): Promise<void> {
    try { await this.directory.removeEntry(sha); }
    catch (error) { if (!(error instanceof DOMException && error.name === 'NotFoundError')) throw error; }
  }
}
